/* Workspace Home — cross-org list + invitations inbox */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ChevronRight, Building2, Inbox } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API } from '../../utils/api';
import { RoleChip, wsErr, roleLabel } from './workspaceUi';
import { fmtDate } from '../owner/ownerUi';

export function WsHome({ me, t, lang, headers, reload, onOpenOrg }) {
  const [busy, setBusy] = useState(null);
  const invitations = me.pending_invitations || [];
  const orgs = me.workspaces || [];

  const respond = async (inv, action) => {
    setBusy(inv.id);
    try {
      await axios.post(`${API}/invitations/${inv.id}/${action}`, {}, { headers });
      toast.success(action === 'accept' ? t.inv_accepted : t.inv_rejected);
      reload();
    } catch (e) { toast.error(wsErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      {invitations.length > 0 && (
        <section data-testid="ws-invitations">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
            <Mail size={13} />{t.invitations}
          </h2>
          <div className="space-y-2">
            {invitations.map((inv) => (
              <div key={inv.id} data-testid={`ws-invitation-${inv.id}`}
                   className="bg-white border border-amber-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold truncate">{inv.org_name}</div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                      {roleLabel(inv.role, t)} · {inv.scope_name || t.whole_org}
                    </div>
                    {inv.invited_by_name && (
                      <div className="text-[11px] text-slate-400 mt-0.5">{t.invited_by} {inv.invited_by_name}</div>
                    )}
                    {inv.expires_at && (
                      <div className="text-[11px] text-slate-400">{t.expires}: {fmtDate(inv.expires_at, lang)}</div>
                    )}
                  </div>
                  <RoleChip role={inv.role} t={t} />
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" data-testid={`ws-accept-${inv.id}`} disabled={busy === inv.id}
                          className="flex-1 h-9 bg-teal-700 hover:bg-teal-800 text-white"
                          onClick={() => respond(inv, 'accept')}>{t.accept}</Button>
                  <Button size="sm" variant="outline" data-testid={`ws-reject-${inv.id}`} disabled={busy === inv.id}
                          className="flex-1 h-9 border-slate-200 text-slate-600"
                          onClick={() => respond(inv, 'reject')}>{t.reject}</Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="ws-org-list">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
          <Building2 size={13} />{t.your_orgs}
        </h2>
        {orgs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center shadow-sm" data-testid="ws-empty-state">
            <span className="w-12 h-12 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
              <Inbox size={22} />
            </span>
            <h3 className="text-[15px] font-semibold mt-3">{t.no_orgs_title}</h3>
            <p className="text-[13px] text-slate-400 mt-1">{t.no_orgs_body}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {orgs.map((org) => {
              const roles = [...new Set((org.roles || []).map((r) => r.role))];
              const divs = (org.responsibilities || []).filter((r) => r.type === 'DIVISION').length;
              const evts = (org.responsibilities || []).filter((r) => r.type === 'EVENT').length;
              return (
                <button key={org.org_id} data-testid={`ws-org-${org.org_id}`}
                        onClick={() => onOpenOrg(org)}
                        className="w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3 text-left hover:border-teal-300 transition-colors">
                  <span className="w-10 h-10 rounded-lg bg-teal-600 text-white text-[13px] font-bold flex items-center justify-center shrink-0">
                    {(org.org_name || '?').slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold truncate">{org.org_name}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {roles.map((r) => <RoleChip key={r} role={r} t={t} />)}
                    </div>
                    <div className="text-[11.5px] text-slate-400 mt-1">
                      {divs} {t.divisions} · {evts} {t.events}
                    </div>
                  </div>
                  <ChevronRight size={17} className="text-slate-300 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
